import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  Search,
  Filter,
  Pencil,
  Trash2,
  CheckCircle2,
  XCircle,
  Clock,
  RefreshCw,
  CalendarDays,
  Linkedin,
  X,
  Loader2,
  Save,
  AlertTriangle,
  ShieldCheck,
  Send,
  Coins,
} from 'lucide-react';
import { useFormController } from '../../Shared/Component/readFormData/useFormController';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';
import Pagination from '../../Shared/Pagination/Pagination';
import { initialUser, UserForm } from '../../App/Types/UsersType';
import {
  approveUserByAdmin,
  deleteUserByAdmin,
  rejectUserByAdmin,
  sendLaunchEmailToPlatformUsers,
  topUpCredits,
} from '../Services/AdminService';

interface Props {
  data: UserForm[];
  onEditSubmit: (data: UserForm) => void;
  onDeleteConfirm: (email: string) => void;
  onRegisterSubmit: (data: Partial<UserForm>) => void;
  menuName: string;
  role?: string;
  onChanged?: () => void;
}

const PAGE_SIZE = 8;

const labelClass = 'mb-1 block text-[10px] font-black uppercase tracking-widest text-slate-400';
const fieldClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2.5 text-sm font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50 [color-scheme:dark]';

const normalize = (status?: string) => (status || '').toUpperCase();

const statusBadge = (status?: string) => {
  const s = normalize(status);
  if (s === 'ACTIVE' || s === 'APPROVED')
    return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400';
  if (s === 'REJECTED') return 'border-rose-500/30 bg-rose-500/10 text-rose-400';
  if (s === 'UNDER_REVIEW' || s === 'PENDING')
    return 'border-amber-500/30 bg-amber-500/10 text-amber-400';
  return 'border-slate-600/40 bg-slate-700/20 text-slate-300';
};

const UsersAndRecruiterComponent = ({
  data,
  onEditSubmit,
  onDeleteConfirm,
  menuName,
  role,
  onChanged,
}: Props) => {
  const [selectedUser, setSelectedUser] = useState<UserForm | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isSendAllOpen, setIsSendAllOpen] = useState(false);
  const [isTopUpOpen, setIsTopUpOpen] = useState(false);
  const [editJobCredits, setEditJobCredits] = useState(0);
  const [topUpCreditType, setTopUpCreditType] = useState<'INVITE' | 'JOB' | 'BOTH'>('INVITE');
  const [topUpInviteCredits, setTopUpInviteCredits] = useState(0);
  const [topUpJobCredits, setTopUpJobCredits] = useState(0);
  const [topUpSubmitting, setTopUpSubmitting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const targetLabel =
    role === 'candidate'
      ? 'all candidates'
      : role === 'recruiter'
      ? 'all recruiters'
      : 'all users';

  const [search, setSearch] = useState('');
  const [verifyFilter, setVerifyFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [page, setPage] = useState(0);

  const { values, setAllValues, handleValueChange } = useFormController<UserForm>(initialUser);

  const stats = useMemo(() => {
    const active = data.filter((u) => ['ACTIVE', 'APPROVED'].includes(normalize(u.status))).length;
    const review = data.filter((u) =>
      ['UNDER_REVIEW', 'PENDING'].includes(normalize(u.status))
    ).length;
    const rejected = data.filter((u) => normalize(u.status) === 'REJECTED').length;
    return { total: data.length, active, review, rejected };
  }, [data]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return data.filter((u) => {
      const matchesSearch =
        !term ||
        `${u.firstName} ${u.lastName}`.toLowerCase().includes(term) ||
        (u.email || '').toLowerCase().includes(term);
      const matchesVerify =
        verifyFilter === 'ALL' ||
        (verifyFilter === 'VERIFIED' && u.isVerified) ||
        (verifyFilter === 'UNVERIFIED' && !u.isVerified);
      const matchesStatus =
        statusFilter === 'ALL' ||
        (statusFilter === 'ACTIVE' && ['ACTIVE', 'APPROVED'].includes(normalize(u.status))) ||
        (statusFilter === 'UNDER_REVIEW' &&
          ['UNDER_REVIEW', 'PENDING'].includes(normalize(u.status))) ||
        normalize(u.status) === statusFilter;
      return matchesSearch && matchesVerify && matchesStatus;
    });
  }, [data, search, verifyFilter, statusFilter]);

  const totalElements = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalElements / PAGE_SIZE));
  const paged = useMemo(
    () => filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE),
    [filtered, page]
  );

  useEffect(() => {
    setPage(0);
  }, [search, verifyFilter, statusFilter]);

  useEffect(() => {
    if (page > 0 && page >= totalPages) {
      setPage(totalPages - 1);
    }
  }, [page, totalPages]);

  const getValidUrl = (url: string) => {
    if (!url) return '#';
    return url.startsWith('http') ? url : `https://${url}`;
  };

  const showAlert = (variant: AlertProps['variant'], title: string, message: string) =>
    setAlert({ variant, title, message, autoCloseMs: 5000, onClose: () => setAlert(null) });

  const handleEditClick = (user: UserForm) => {
    setSelectedUser(user);
    setAllValues({
      id: user.id || '',
      email: user.email || '',
      password: user.password || '',
      role: user.role || '',
      isVerified: user.isVerified ?? false,
      status: user.status || '',
      createdAt: user.createdAt || '',
      updatedAt: user.updatedAt || '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      linkedInUrl: user.linkedInUrl || '',
      message: user.message || '',
      invitedByUserId: user.invitedByUserId || '',
      inviteCountRemaining: user.inviteCountRemaining ?? 0,
      inviteCountTotal: user.inviteCountTotal ?? 0,
      userRole: user.userRole || '',
    });
    setEditJobCredits(0);
    setIsEditOpen(true);
  };

  const handleDeleteClick = (user: UserForm) => {
    setSelectedUser(user);
    setIsDeleteOpen(true);
  };

  const handleEditSubmit = async () => {
    setAlert(null);
    setSubmitting(true);
    try {
      const payload = {
        email: values.email,
        message: values.message,
        inviteCountRemaining: values.inviteCountRemaining,
        jobCredits: editJobCredits > 0 ? editJobCredits : null,
      };

      const response =
        normalize(values.status) === 'REJECTED'
          ? await rejectUserByAdmin(payload)
          : await approveUserByAdmin(payload);

      if (response.success) {
        showAlert(
          'success',
          'Success...!',
          `${response.data?.firstName || values.firstName} has been updated successfully.`
        );
        onEditSubmit(values);
        onChanged?.();
      } else {
        showAlert('error', 'Update Error', response.message);
      }
    } finally {
      setSubmitting(false);
      setIsEditOpen(false);
    }
  };

  const handleDeleteSubmit = async () => {
    if (!selectedUser) return;
    setAlert(null);
    setSubmitting(true);
    try {
      const response = await deleteUserByAdmin(selectedUser.id);
      const failed = (response as any)?.success === false;
      if (!failed) {
        showAlert('success', 'Success...!', `${selectedUser.email} has been deleted successfully.`);
        onDeleteConfirm(selectedUser.email);
        onChanged?.();
      } else {
        showAlert('error', 'Delete Error', response.message || 'Delete failed');
      }
    } finally {
      setSubmitting(false);
      setIsDeleteOpen(false);
    }
  };

  const handleTopUpClick = (user: UserForm) => {
    setSelectedUser(user);
    setTopUpCreditType('INVITE');
    setTopUpInviteCredits(0);
    setTopUpJobCredits(0);
    setIsTopUpOpen(true);
  };

  const handleTopUpSubmit = async () => {
    if (!selectedUser) return;
    if (topUpCreditType === 'INVITE' && topUpInviteCredits <= 0) {
      showAlert('error', 'Invalid Amount', 'Enter invite credits greater than zero.');
      return;
    }
    if (topUpCreditType === 'JOB' && topUpJobCredits <= 0) {
      showAlert('error', 'Invalid Amount', 'Enter job credits greater than zero.');
      return;
    }
    if (topUpCreditType === 'BOTH' && (topUpInviteCredits <= 0 || topUpJobCredits <= 0)) {
      showAlert('error', 'Invalid Amount', 'Enter both invite and job credits greater than zero.');
      return;
    }
    setAlert(null);
    setTopUpSubmitting(true);
    try {
      const response = await topUpCredits({
        userId: selectedUser.id,
        creditType: topUpCreditType,
        inviteCredits: topUpInviteCredits,
        jobCredits: topUpJobCredits,
      });
      if (response.success) {
        showAlert(
          'success',
          'Credits Added...!',
          `${selectedUser.email}: ${response.data?.message || 'credits added successfully.'}`
        );
        setIsTopUpOpen(false);
        onChanged?.();
      } else {
        showAlert('error', 'Top Up Error', response.message);
      }
    } catch {
      showAlert('error', 'Top Up Error', 'Failed to add credits. Please try again.');
    } finally {
      setTopUpSubmitting(false);
    }
  };

  const handleSendAll = async () => {
    setAlert(null);
    setSendingAll(true);
    try {
      const response = await sendLaunchEmailToPlatformUsers(role || '');
      const list = (response as any)?.data;
      const sent = Array.isArray(list) ? list.length : 0;
      showAlert(
        'success',
        'Success...!',
        `Launch email sent to ${sent} ${targetLabel}.`
      );
      setIsSendAllOpen(false);
      onChanged?.();
    } catch {
      showAlert('error', 'Send Error', 'Failed to send emails. Please try again.');
    } finally {
      setSendingAll(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
            <ShieldCheck size={22} />
          </span>
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">
              {menuName} Directory
            </h1>
            <p className="text-xs font-semibold text-slate-400 md:text-sm">
              All users with this role
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setAlert(null);
              setIsSendAllOpen(true);
            }}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-sm font-bold text-emerald-400 transition hover:bg-emerald-500/20"
          >
            <Send size={16} />
            Send All
          </button>
          <button
            onClick={() => onChanged?.()}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-sm font-bold text-slate-200 transition hover:border-cyan-500/40 hover:text-cyan-400"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[
          { label: 'Total Users', value: stats.total, icon: <Users size={18} />, accent: 'text-cyan-400 border-cyan-500/30 bg-cyan-500/10' },
          { label: 'Active', value: stats.active, icon: <CheckCircle2 size={18} />, accent: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10' },
          { label: 'Under Review', value: stats.review, icon: <Clock size={18} />, accent: 'text-amber-400 border-amber-500/30 bg-amber-500/10' },
          { label: 'Rejected', value: stats.rejected, icon: <XCircle size={18} />, accent: 'text-rose-400 border-rose-500/30 bg-rose-500/10' },
        ].map((card) => (
          <div key={card.label} className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
            <div className="flex items-center justify-between">
              <span className={`flex h-9 w-9 items-center justify-center rounded-xl border ${card.accent}`}>
                {card.icon}
              </span>
              <span className="text-2xl font-black text-slate-100">{card.value}</span>
            </div>
            <p className="mt-2 text-[11px] font-bold uppercase tracking-widest text-slate-500">
              {card.label}
            </p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="mb-6 rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email..."
              className={`${fieldClass} pl-9`}
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-500" />
            <select
              value={verifyFilter}
              onChange={(e) => setVerifyFilter(e.target.value)}
              className={fieldClass}
            >
              <option value="ALL">All Verification</option>
              <option value="VERIFIED">Verified</option>
              <option value="UNVERIFIED">Unverified</option>
            </select>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {[
              { label: 'All', value: 'ALL' },
              { label: 'Active', value: 'ACTIVE' },
              { label: 'Under Review', value: 'UNDER_REVIEW' },
              { label: 'Rejected', value: 'REJECTED' },
            ].map((pill) => (
              <button
                key={pill.value}
                onClick={() => setStatusFilter(pill.value)}
                className={`rounded-xl border px-3 py-2 text-xs font-bold transition ${
                  statusFilter === pill.value
                    ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-400'
                    : 'border-slate-800 bg-slate-950/60 text-slate-400 hover:text-slate-200'
                }`}
              >
                {pill.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {alert && (
        <div className="mb-6">
          <Alert {...alert} onClose={() => setAlert(null)} />
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/40">
        <table className="w-full min-w-[900px] text-left">
          <thead>
            <tr className="border-b border-slate-800 bg-slate-900/60">
              <th className="px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Full Name</th>
              <th className="px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Email</th>
              <th className="px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Role</th>
              <th className="px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Status</th>
              <th className="px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">LinkedIn</th>
              <th className="px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Joined</th>
              <th className="px-5 py-4 text-right text-[10px] font-black uppercase tracking-widest text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/70">
            {paged.map((user, idx) => (
              <motion.tr
                key={user.id || user.email || idx}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 }}
                className="transition hover:bg-slate-900/70"
              >
                <td className="px-5 py-4">
                  <span className="text-sm font-bold text-slate-100">
                    {user.firstName} {user.lastName}
                  </span>
                </td>
                <td className="px-5 py-4">
                  {user.email ? (
                    <span title={user.email} className="text-sm font-semibold text-cyan-400">
                      {user.email.length > 22 ? `${user.email.slice(0, 22)}...` : user.email}
                    </span>
                  ) : (
                    <span className="text-slate-600">?</span>
                  )}
                </td>
                <td className="px-5 py-4">
                  <span className="text-sm font-semibold text-slate-300">{user.role || '?'}</span>
                </td>
                <td className="px-5 py-4">
                  <span
                    className={`inline-flex rounded-lg border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${statusBadge(
                      user.status
                    )}`}
                  >
                    {user.status || 'UNKNOWN'}
                  </span>
                </td>
                <td className="px-5 py-4">
                  {user.linkedInUrl ? (
                    <a
                      href={getValidUrl(user.linkedInUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={user.linkedInUrl}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-400 hover:underline"
                    >
                      <Linkedin size={14} />
                      {user.linkedInUrl.length > 16
                        ? `${user.linkedInUrl.slice(0, 16)}...`
                        : user.linkedInUrl}
                    </a>
                  ) : (
                    <span className="text-slate-600">?</span>
                  )}
                </td>
                <td className="px-5 py-4">
                  <span className="flex items-center gap-2 text-sm font-semibold text-slate-400">
                    <CalendarDays size={14} className="text-slate-600" />
                    {user.createdAt
                      ? new Date(user.createdAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      : 'N/A'}
                  </span>
                </td>
                <td className="px-5 py-4">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => handleTopUpClick(user)}
                      title="Give Credits"
                      className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-2 text-amber-400 transition hover:bg-amber-500/20"
                    >
                      <Coins size={16} />
                    </button>
                    <button
                      onClick={() => handleEditClick(user)}
                      title="Review / Update"
                      className="rounded-xl border border-cyan-500/30 bg-cyan-500/10 p-2 text-cyan-400 transition hover:bg-cyan-500/20"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(user)}
                      title="Delete"
                      className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-2 text-rose-400 transition hover:bg-rose-500/20"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </motion.tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-5 py-16 text-center text-sm font-semibold text-slate-500">
                  No users match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalElements > 0 && (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/40 px-4 py-2">
          <Pagination
            page={page}
            size={PAGE_SIZE}
            totalElements={totalElements}
            totalPages={totalPages}
            first={page === 0}
            last={page >= totalPages - 1}
            hasNext={page < totalPages - 1}
            hasPrevious={page > 0}
            setPage={setPage}
          />
        </div>
      )}

      {/* Edit / Review modal */}
      <AnimatePresence>
        {isEditOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
            onClick={() => setIsEditOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl"
            >
              <div className="mb-6 flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-black text-slate-100">
                    Review {selectedUser?.firstName} {selectedUser?.lastName}
                  </h2>
                  <p className="text-xs font-semibold text-slate-400">{menuName} account decision</p>
                </div>
                <button
                  onClick={() => setIsEditOpen(false)}
                  className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-800 hover:text-slate-200"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Full Name</label>
                  <input readOnly value={`${values.firstName} ${values.lastName}`} className={`${fieldClass} text-slate-400`} />
                </div>
                <div>
                  <label className={labelClass}>Email</label>
                  <input readOnly value={values.email} className={`${fieldClass} text-slate-400`} />
                </div>
                <div>
                  <label className={labelClass}>Status</label>
                  <select
                    value={values.status}
                    onChange={(e) => handleValueChange('status')(e.target.value)}
                    className={fieldClass}
                  >
                    <option value="">Select status</option>
                    <option value="ACTIVE">Approve</option>
                    <option value="REJECTED">Reject</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Message (for rejection)</label>
                  <textarea
                    rows={3}
                    value={values.message?.toString() || ''}
                    onChange={(e) => handleValueChange('message')(e.target.value)}
                    placeholder="Enter the message for rejection"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Invite Credits</label>
                  <input
                    readOnly
                    value={values.inviteCountRemaining}
                    className={`${fieldClass} text-slate-400`}
                  />
                  <p className="mt-1 text-[10px] font-semibold text-slate-500">
                    {values.inviteCountRemaining} remaining of {values.inviteCountTotal} granted
                  </p>
                </div>
                <div>
                  <label className={labelClass}>Job Credits (recruiter only)</label>
                  <input
                    type="number"
                    value={editJobCredits}
                    onChange={(e) => setEditJobCredits(Number(e.target.value) || 0)}
                    placeholder="0 = keep existing company balance"
                    className={fieldClass}
                  />
                  <p className="mt-1 text-[10px] font-semibold text-slate-500">
                    On approve, this replaces the company job-credit balance (recruiters).
                  </p>
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setIsEditOpen(false)}
                  className="flex-1 rounded-xl border border-slate-800 bg-slate-950/60 py-3 text-sm font-bold text-slate-300 transition hover:text-slate-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditSubmit}
                  disabled={submitting}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-cyan-500 py-3 text-sm font-black text-slate-950 transition hover:bg-cyan-400 disabled:opacity-60"
                >
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  Update
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top-up credits modal */}
      <AnimatePresence>
        {isTopUpOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
            onClick={() => setIsTopUpOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl"
            >
              <div className="mb-6 flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-black text-slate-100">Give Credits</h2>
                  <p className="text-xs font-semibold text-slate-400">
                    {selectedUser?.firstName} {selectedUser?.lastName} · {selectedUser?.email}
                  </p>
                </div>
                <button
                  onClick={() => setIsTopUpOpen(false)}
                  className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-800 hover:text-slate-200"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="mb-5 flex gap-2">
                {(['INVITE', 'JOB', 'BOTH'] as const).map((type) => (
                  <button
                    key={type}
                    onClick={() => setTopUpCreditType(type)}
                    className={`flex-1 rounded-xl border px-3 py-2.5 text-xs font-black uppercase tracking-wider transition ${
                      topUpCreditType === type
                        ? 'border-amber-500/50 bg-amber-500/10 text-amber-400'
                        : 'border-slate-800 bg-slate-950/60 text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>

              <div className="space-y-4">
                {topUpCreditType !== 'JOB' && (
                  <div>
                    <label className={labelClass}>Invite Credits to Add</label>
                    <input
                      type="number"
                      min={1}
                      value={topUpInviteCredits}
                      onChange={(e) => setTopUpInviteCredits(Number(e.target.value) || 0)}
                      className={fieldClass}
                    />
                    <p className="mt-1 text-[10px] font-semibold text-slate-500">
                      Current invite balance: {selectedUser?.inviteCountRemaining ?? 0} of{' '}
                      {selectedUser?.inviteCountTotal ?? 0} granted
                    </p>
                    <p className="mt-0.5 text-[10px] font-semibold text-slate-600">
                      Adding credits raises both the balance and the granted total.
                    </p>
                  </div>
                )}
                {topUpCreditType !== 'INVITE' && (
                  <div>
                    <label className={labelClass}>Job Credits to Add (recruiter)</label>
                    <input
                      type="number"
                      min={1}
                      value={topUpJobCredits}
                      onChange={(e) => setTopUpJobCredits(Number(e.target.value) || 0)}
                      className={fieldClass}
                    />
                    <p className="mt-1 text-[10px] font-semibold text-slate-500">
                      Adds to the recruiter's company balance. Amounts are additive.
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setIsTopUpOpen(false)}
                  className="flex-1 rounded-xl border border-slate-800 bg-slate-950/60 py-3 text-sm font-bold text-slate-300 transition hover:text-slate-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleTopUpSubmit}
                  disabled={topUpSubmitting}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-amber-500 py-3 text-sm font-black text-slate-950 transition hover:bg-amber-400 disabled:opacity-60"
                >
                  {topUpSubmitting ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Coins size={16} />
                  )}
                  Give Credits
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete modal */}
      <AnimatePresence>
        {isDeleteOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
            onClick={() => setIsDeleteOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-6 text-center shadow-2xl"
            >
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl border border-rose-500/30 bg-rose-500/10 text-rose-400">
                <AlertTriangle size={28} />
              </div>
              <h2 className="text-lg font-black text-slate-100">Confirm Removal</h2>
              <p className="mt-2 px-2 text-sm font-medium text-slate-400">
                Are you sure you want to delete{' '}
                <span className="font-bold text-slate-100">
                  {selectedUser?.firstName} {selectedUser?.lastName}
                </span>
                ?
              </p>
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setIsDeleteOpen(false)}
                  className="flex-1 rounded-xl border border-slate-800 bg-slate-950/60 py-3 text-sm font-bold text-slate-300 transition hover:text-slate-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteSubmit}
                  disabled={submitting}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-rose-500 py-3 text-sm font-black text-white transition hover:bg-rose-400 disabled:opacity-60"
                >
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Send All modal */}
      <AnimatePresence>
        {isSendAllOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
            onClick={() => setIsSendAllOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-6 text-center shadow-2xl"
            >
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
                <Send size={28} />
              </div>
              <h2 className="text-lg font-black text-slate-100">Send Launch Email</h2>
              <p className="mt-2 px-2 text-sm font-medium text-slate-400">
                Send the &quot;NexGent is Officially Live&quot; email to{' '}
                <span className="font-bold text-emerald-400">{targetLabel}</span> in one shot?
              </p>
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setIsSendAllOpen(false)}
                  className="flex-1 rounded-xl border border-slate-800 bg-slate-950/60 py-3 text-sm font-bold text-slate-300 transition hover:text-slate-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendAll}
                  disabled={sendingAll}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-emerald-500 py-3 text-sm font-black text-slate-950 transition hover:bg-emerald-400 disabled:opacity-60"
                >
                  {sendingAll ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  Send All
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UsersAndRecruiterComponent;
